import { SkyMessageBar } from "@skypointcloud/skypoint-ui";
import { Link } from "wouter";
import { MONITOR_INCIDENTS_PATH } from "@shared/types/monitor";
import { usePermissions } from "@/hooks/use-permissions";
import { useMonitorEnabled, useRunIncident } from "@/hooks/use-monitor";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { absolutePath } from "@/lib/routes";
import { formatInstant } from "@/lib/time/displayTime";

/**
 * #842: on a failed or partly failed scheduled run, the schedule's open skyMonitor alert that
 * covers it (the server decides: a run of the open episode only), linked to skyMonitor's
 * Incidents tab. Nothing while skyMonitor is off, for a reader without `monitor.read`, for any
 * other run, or when the read fails: it is a pointer, never a blocker.
 */
export function RunIncidentNote({
  runId,
  status,
  scheduleId,
}: {
  runId: string;
  status: string;
  scheduleId: string | null;
}) {
  const { canAny } = usePermissions();
  const monitorOn = useMonitorEnabled();
  const tz = useDisplayTz();
  const covered = status === "failed" || status === "partially_succeeded";
  const incident = useRunIncident(
    runId,
    monitorOn && canAny("monitor.read") && covered && scheduleId !== null,
  );
  const open = incident.data?.incident;
  if (!open) return null;
  return (
    <SkyMessageBar
      intent="warning"
      layout="multiline"
      title="skyMonitor has an open alert for this schedule"
      message={`This run is one of the failed runs it covers. The alert opened ${formatInstant(open.openedAt, tz, "short", { named: true })}.`}
      actions={
        <Link
          href={absolutePath(MONITOR_INCIDENTS_PATH)}
          className="font-medium underline underline-offset-2"
          data-testid="run-incident-link"
        >
          Open the alert
        </Link>
      }
      testId="run-incident-note"
    />
  );
}
